import { useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { SubscriptionTier, SubscriptionStatus } from "@/lib/billing/plans";
import type { SubscriptionFeatureFlags } from "@/hooks/useSubscription";

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * 24-hour client-side grace buffer applied on top of the server's configured
 * grace period. This covers the window between Polar.sh marking a subscription
 * as expired and the webhook being processed and written to Convex.
 * Without this buffer, users could lose access momentarily even though the
 * subscription is still valid from Polar's perspective.
 */
const CLIENT_GRACE_BUFFER_MS = 24 * 60 * 60 * 1000; // 24 hours

// ─── Types ────────────────────────────────────────────────────────────────────

export type GateReason =
  | "auth_required"
  | "user_not_found"
  | "no_active_subscription"
  | "tier_insufficient"
  | "active"
  | "grace_period"
  | "canceled_but_in_period"
  | "admin";

export type MinTierRequirement = "any_paid" | "plus" | "pro";

export type SubscriptionGateResult = {
  /** True while Clerk auth or Convex queries are still loading */
  isLoading: boolean;
  /** Whether the user is permitted to access the gated content */
  permitted: boolean;
  /** Current subscription tier */
  tier: SubscriptionTier;
  /** Current Polar.sh subscription status */
  status: SubscriptionStatus;
  /**
   * True when the user is inside the 24-hour client-side grace buffer.
   * Shown with a SubscriptionBanner instead of blocking access so users
   * have time to act before the next request to Convex confirms expiry.
   */
  isInGracePeriod: boolean;
  /**
   * When the client-side 24-hour grace window closes (ms epoch).
   * null when the user is not in the client-side grace buffer.
   */
  clientGraceEndsAt: number | null;
  /** Granular feature flags derived from the subscription tier */
  featureFlags: SubscriptionFeatureFlags;
  /** The reason for the gate decision (for constructing targeted redirect URLs) */
  reason: GateReason;
  /** Where to redirect if the user is not permitted */
  redirectPath: string;
  /** True when the user has an admin override bypassing billing */
  isAdmin: boolean;
  /** The Polar.sh subscription ID. null when no subscription exists. */
  polarSubscriptionId: string | null;
  /** When the current billing period ends (ms epoch). null when unknown. */
  currentPeriodEnd: number | null;
  /**
   * True when the user is on a free trial (status === "trialing").
   * Exposed so SubscriptionGate can inject this into the context it provides
   * to child components.
   */
  isTrialing: boolean;
};

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_FEATURE_FLAGS: SubscriptionFeatureFlags = {
  serverLimit: 0,
  canCreateOrg: false,
  canViewAdvancedStats: false,
  canAccessPriorityQueue: false,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useSubscriptionGate
 *
 * The core Polar.sh subscription gating hook. Acts as the middleware-equivalent
 * for React Router SPAs, evaluating whether the current user may access a
 * premium-gated route.
 *
 * Data sources (in order of authority):
 * 1. Clerk — authentication status
 * 2. `billing.getMyEntitlement` (Convex) — server-side access decision, grace
 *    period logic, and feature flags, reading from the `users` denormalized table
 * 3. `billing.getMySubscription` (Convex) — raw subscription record from the
 *    authoritative `subscriptions` table (the "polling task" output written by
 *    Polar.sh webhooks via `billingCrons` and `http.ts`)
 *
 * In addition to the server-side grace period (configured via
 * `SUBSCRIPTION_GATE_GRACE_DAYS`), this hook applies a 24-hour client-side
 * buffer. This protects against the webhook processing delay window: if Polar
 * marks a subscription expired but Convex hasn't received or processed the
 * webhook yet, users retain access for up to 24 hours rather than being
 * incorrectly blocked.
 *
 * Tier gating:
 * - "any_paid": any non-free active subscription (default)
 * - "plus":     Plus, Pro, or Enterprise tier required
 * - "pro":      Pro or Enterprise tier required
 *
 * @param nextPath - The current route path, used in redirect URLs for post-login return
 * @param minTier  - Minimum subscription tier required. Defaults to "any_paid".
 */
export function useSubscriptionGate(
  nextPath: string,
  minTier: MinTierRequirement = "any_paid"
): SubscriptionGateResult {
  const { isLoaded, isSignedIn } = useAuth();

  // Server-side entitlement decision (grace period + feature flags from users table)
  const entitlement = useQuery(
    api.billing.getMyEntitlement,
    isSignedIn ? {} : "skip"
  );

  // Authoritative subscription record from the subscriptions table
  // (populated by Polar.sh webhooks processed in billingCrons / http.ts)
  const subscription = useQuery(
    api.billing.getMySubscription,
    isSignedIn ? {} : "skip"
  );

  return useMemo<SubscriptionGateResult>(() => {
    // ── Loading state ─────────────────────────────────────────────────────
    if (!isLoaded) {
      return {
        isLoading: true,
        permitted: false,
        tier: "free",
        status: "none",
        isInGracePeriod: false,
        clientGraceEndsAt: null,
        featureFlags: DEFAULT_FEATURE_FLAGS,
        reason: "auth_required",
        redirectPath: buildPricingRedirect("auth_required", nextPath),
        isAdmin: false,
        polarSubscriptionId: null,
        currentPeriodEnd: null,
        isTrialing: false,
      };
    }

    // Convex queries still resolving (user is signed in but data not yet available)
    if (isSignedIn && (entitlement === undefined || subscription === undefined)) {
      return {
        isLoading: true,
        permitted: false,
        tier: "free",
        status: "none",
        isInGracePeriod: false,
        clientGraceEndsAt: null,
        featureFlags: DEFAULT_FEATURE_FLAGS,
        reason: "auth_required",
        redirectPath: buildPricingRedirect("auth_required", nextPath),
        isAdmin: false,
        polarSubscriptionId: null,
        currentPeriodEnd: null,
        isTrialing: false,
      };
    }

    // ── Unauthenticated ───────────────────────────────────────────────────
    if (!isSignedIn || entitlement === null || entitlement === undefined) {
      return buildDeniedResult(
        "auth_required",
        buildPricingRedirect("auth_required", nextPath)
      );
    }

    // ── Admin bypass ──────────────────────────────────────────────────────
    if ((entitlement as { reason?: string }).reason === "admin") {
      return {
        isLoading: false,
        permitted: true,
        tier: entitlement.tier as SubscriptionTier,
        status: entitlement.status as SubscriptionStatus,
        isInGracePeriod: false,
        clientGraceEndsAt: null,
        featureFlags: (entitlement.featureFlags as SubscriptionFeatureFlags) ?? DEFAULT_FEATURE_FLAGS,
        reason: "admin",
        redirectPath: "",
        isAdmin: true,
        polarSubscriptionId: subscription?.polarSubscriptionId ?? null,
        currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
        isTrialing: false,
      };
    }

    // ── Server says no access ─────────────────────────────────────────────
    // Apply 24-hour client-side grace buffer on top of the server decision.
    // This covers the webhook processing window where Polar has already updated
    // the subscription state but Convex hasn't received the event yet.
    if (!entitlement.hasAccess) {
      const now = Date.now();
      const subEndsAt =
        subscription?.endsAt ?? subscription?.currentPeriodEnd ?? null;

      const isInClientGrace =
        subEndsAt !== null &&
        now >= subEndsAt &&
        now < subEndsAt + CLIENT_GRACE_BUFFER_MS;

      if (isInClientGrace) {
        const clientGraceEndsAt = subEndsAt + CLIENT_GRACE_BUFFER_MS;
        const tier = (subscription?.tier ?? entitlement.tier) as SubscriptionTier;
        const graceStatus = (subscription?.status ?? entitlement.status) as SubscriptionStatus;
        return {
          isLoading: false,
          permitted: true,
          tier,
          status: graceStatus,
          isInGracePeriod: true,
          clientGraceEndsAt,
          featureFlags:
            (entitlement.featureFlags as SubscriptionFeatureFlags) ?? DEFAULT_FEATURE_FLAGS,
          reason: "grace_period",
          redirectPath: "",
          isAdmin: false,
          polarSubscriptionId: subscription?.polarSubscriptionId ?? null,
          currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
          isTrialing: graceStatus === "trialing",
        };
      }

      // No access and outside client grace buffer — redirect
      const deniedReason = (entitlement.reason ?? "no_active_subscription") as GateReason;
      return buildDeniedResult(
        deniedReason,
        buildRedirectForReason(deniedReason, nextPath)
      );
    }

    // ── Access granted — check tier requirement ───────────────────────────
    const tier = entitlement.tier as SubscriptionTier;

    if (!tierMeetsRequirement(tier, minTier)) {
      return buildDeniedResult(
        "tier_insufficient",
        buildUpgradeRedirect(minTier, nextPath)
      );
    }

    // ── Permitted ─────────────────────────────────────────────────────────
    const reason = (entitlement.isInGracePeriod ? "grace_period" : entitlement.reason ?? "active") as GateReason;
    const permittedStatus = entitlement.status as SubscriptionStatus;

    return {
      isLoading: false,
      permitted: true,
      tier,
      status: permittedStatus,
      isInGracePeriod: entitlement.isInGracePeriod ?? false,
      clientGraceEndsAt: null,
      featureFlags:
        (entitlement.featureFlags as SubscriptionFeatureFlags) ?? DEFAULT_FEATURE_FLAGS,
      reason,
      redirectPath: "",
      isAdmin: false,
      polarSubscriptionId: subscription?.polarSubscriptionId ?? null,
      currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
      isTrialing: permittedStatus === "trialing",
    };
  }, [isLoaded, isSignedIn, entitlement, subscription, nextPath, minTier]);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function buildDeniedResult(
  reason: GateReason,
  redirectPath: string
): SubscriptionGateResult {
  return {
    isLoading: false,
    permitted: false,
    tier: "free",
    status: "none",
    isInGracePeriod: false,
    clientGraceEndsAt: null,
    featureFlags: DEFAULT_FEATURE_FLAGS,
    reason,
    redirectPath,
    isAdmin: false,
    polarSubscriptionId: null,
    currentPeriodEnd: null,
    isTrialing: false,
  };
}

/** Returns true when the given tier meets the minimum required tier level */
function tierMeetsRequirement(
  tier: SubscriptionTier,
  required: MinTierRequirement
): boolean {
  if (required === "any_paid") return tier !== "free";
  if (required === "plus")
    return ["plus", "pro", "enterprise"].includes(tier);
  if (required === "pro")
    return ["pro", "enterprise"].includes(tier);
  return false;
}

/**
 * Builds a redirect to /upgrade (alias for /pricing) with reason + return path.
 * The subscription gate uses /upgrade as the canonical redirect target so that
 * the destination can be swapped to a dedicated upgrade landing page later
 * without touching every gated route.
 */
function buildPricingRedirect(reason: string, next: string): string {
  const q = new URLSearchParams({
    upgrade: "1",
    reason,
    next,
    source: "subscription_gate",
  });
  return `/upgrade?${q.toString()}`;
}

/**
 * Chooses between /upgrade and /upgrade?plan=... based on the denial reason.
 * Users who have never subscribed are sent to the pricing/upgrade page;
 * users who had an active subscription but it lapsed get the same page with a
 * renewal-prompt reason so the UI can tailor the CTA copy.
 */
function buildRedirectForReason(reason: GateReason, next: string): string {
  return buildPricingRedirect(reason, next);
}

/** Builds a /upgrade redirect that highlights the required upgrade tier */
function buildUpgradeRedirect(minTier: MinTierRequirement, next: string): string {
  const q = new URLSearchParams({
    upgrade: "1",
    reason: "tier_insufficient",
    plan: minTier,
    next,
    source: "subscription_gate",
  });
  return `/upgrade?${q.toString()}`;
}
